import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { readUnresolvedReferences } from "../../../../utils/reflectionScenarioOptions";
import useLongAction from "../../../shared/long-action/useLongAction";
import ActionPanel from "../../../shared/long-action/ActionPanel";

const TYPE_LABELS = {
  user_info_field: "User Info Field",
  classification_question: "Classification Question",
  skill_group: "Skill Group",
  skill: "Skill",
};

const describeSourceMetadata = (conflict) => {
  const metadata = conflict?.source_metadata || {};
  if (conflict?.type === "user_info_field") {
    return metadata.question || "";
  }
  if (conflict?.type === "classification_question") {
    return metadata.definition || "";
  }
  if (conflict?.type === "skill_group") {
    return metadata.description || "";
  }
  if (conflict?.type === "skill") {
    const groupName = metadata.skill_group_name ? `Group: ${metadata.skill_group_name}` : "";
    const description = metadata.description || "";
    return [groupName, description].filter(Boolean).join(" | ");
  }
  return "";
};

const describeCandidate = (conflict, candidate) => {
  if (conflict?.type === "user_info_field") {
    return candidate?.question || "";
  }
  if (conflict?.type === "classification_question") {
    return candidate?.definition || "";
  }
  if (conflict?.type === "skill_group") {
    return candidate?.description || "";
  }
  if (conflict?.type === "skill") {
    const groupName = candidate?.skill_group_name ? `Group: ${candidate.skill_group_name}` : "";
    const description = candidate?.description || "";
    return [groupName, description].filter(Boolean).join(" | ");
  }
  return "";
};

const DependencyResolutionDialog = ({
  open,
  onClose,
  onConfirm,
  previewData,
  loading,
  entityLabel = "Scenario",
  entityNameKey = "scenario_name",
  confirmLabel = null,
}) => {
  const { enabled } = useLongAction();
  const conflicts = useMemo(
    () => (Array.isArray(previewData?.dependency_conflicts) ? previewData.dependency_conflicts : []),
    [previewData]
  );
  /**
   * Refs the bundle cannot resolve on its own. There is one kind: a lesson's `reflects_on_scenario_ref`.
   *
   * A different question from a dependency conflict, which is why the server reports it under its
   * own key: a conflict says *"this name already exists, choose"*, an unresolved reference says
   * *"this target did not travel in the bundle, point me at it"*. Import REJECTS a bundle whose
   * refs are unresolved (422) rather than importing it as an ordinary unlinked lesson, so without
   * this section such a bundle simply cannot be imported through the UI at all.
   */
  const unresolvedReferences = useMemo(
    () => readUnresolvedReferences(previewData),
    [previewData]
  );
  const resolvedConfirmLabel = confirmLabel || `Import ${entityLabel}`;
  const entityName = previewData?.[entityNameKey] || previewData?.scenario_name || previewData?.unit_name || "";
  const [resolutions, setResolutions] = useState({});
  const [contentResolutions, setContentResolutions] = useState({});

  useEffect(() => {
    if (!open) return;
    const nextResolutions = {};
    conflicts.forEach((conflict) => {
      nextResolutions[conflict.ref] = {
        action: conflict.default_action || "reuse_existing",
        existing_id: conflict.default_existing_id || "",
        new_name: conflict.suggested_duplicate_name || "",
      };
    });
    setResolutions(nextResolutions);
    // Deliberately no default choice: there is no safe guess for which role play a reflection
    // lesson belongs to, and a wrong link is worse than being asked.
    const nextContentResolutions = {};
    unresolvedReferences.forEach((reference) => {
      nextContentResolutions[reference.ref] = "";
    });
    setContentResolutions(nextContentResolutions);
  }, [conflicts, unresolvedReferences, open]);

  const validationErrors = useMemo(() => {
    const dependencyErrors = conflicts
      .map((conflict) => {
        const resolution = resolutions[conflict.ref] || {};
        if (resolution.action === "reuse_existing") {
          if (!resolution.existing_id) {
            return `${conflict.source_name}: choose an existing item to reuse`;
          }
          return null;
        }
        if (resolution.action === "create_duplicate") {
          if (!String(resolution.new_name || "").trim()) {
            return `${conflict.source_name}: choose a name for the duplicate`;
          }
          return null;
        }
        return `${conflict.source_name}: choose how to resolve this dependency`;
      })
      .filter(Boolean);

    const referenceErrors = unresolvedReferences
      .map((reference) =>
        contentResolutions[reference.ref]
          ? null
          : `${reference.lesson_name || "This lesson"}: choose the role play it reflects on`
      )
      .filter(Boolean);

    return [...dependencyErrors, ...referenceErrors];
  }, [conflicts, contentResolutions, resolutions, unresolvedReferences]);

  const handleConfirm = () => {
    const payload = {};
    conflicts.forEach((conflict) => {
      const resolution = resolutions[conflict.ref] || {};
      payload[conflict.ref] = {
        action: resolution.action,
      };
      if (resolution.action === "reuse_existing" && resolution.existing_id) {
        payload[conflict.ref].existing_id = resolution.existing_id;
      }
      if (resolution.action === "create_duplicate") {
        payload[conflict.ref].new_name = String(resolution.new_name || "").trim();
      }
    });
    // `{ "<ref>": { "scenario_id": <int> } }` — the shape `parse_content_resolutions` expects.
    const contentPayload = {};
    unresolvedReferences.forEach((reference) => {
      const chosen = contentResolutions[reference.ref];
      if (chosen) contentPayload[reference.ref] = { scenario_id: Number(chosen) };
    });
    onConfirm(payload, contentPayload);
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === "backdropClick" || (enabled && loading)) return;
        onClose?.(event, reason);
      }}
      disableEscapeKeyDown={enabled && loading}
      sx={enabled ? { zIndex: 13000 } : undefined}
      maxWidth="md"
      fullWidth
    >
      {enabled && loading ? <ActionPanel working label={`Importing the ${entityLabel.toLowerCase()}…`} /> : <>
      <DialogTitle>
        {conflicts.length === 0 && unresolvedReferences.length > 0
          ? `Link ${entityLabel} Role Play`
          : `Resolve ${entityLabel} Dependencies`}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {conflicts.length === 0 && unresolvedReferences.length > 0
            ? `The ${entityLabel.toLowerCase()}${entityName ? ` "${entityName}"` : ""} reflects on a role play that is not part of this bundle. Point it at the matching role play in this course.`
            : `The ${entityLabel.toLowerCase()} "${entityName}" includes portable dependencies. Review only the items that already exist in the target course.`}
        </Typography>

        {/* Dependency counts are meaningless for a bundle whose only open question is a role-play
            link, and a "0 objects / 0 matches" banner reads like something went wrong. */}
        {conflicts.length > 0 ? (
          <Alert severity="info" sx={{ mb: 3 }}>
            This import will auto-create {previewData?.summary?.auto_create_dependencies || 0} dependency
            objects and needs review for {previewData?.summary?.conflicting_dependencies || 0} existing
            matches.
          </Alert>
        ) : null}

        {unresolvedReferences.length > 0 ? (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              Role play links
            </Typography>
            <Alert severity="warning" sx={{ mb: 2 }}>
              {unresolvedReferences.length === 1
                ? "This bundle contains a reflection lesson whose role play is not part of it. Choose the role play in this course that it reflects on."
                : `This bundle contains ${unresolvedReferences.length} reflection lessons whose role plays are not part of it. Choose the role play in this course that each one reflects on.`}
            </Alert>
            {unresolvedReferences.map((reference) => (
              <Box
                key={reference.ref}
                sx={{ mb: 2, p: 2, border: "1px solid", borderColor: "divider", borderRadius: 1.5 }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {reference.lesson_name || "Reflection lesson"}
                  </Typography>
                  <Chip size="small" color="warning" label="Reflects on role play" />
                </Box>
                {/* `source_name` is the scenario's name in the SOURCE environment, exported purely
                    as a hint. A machine-made export may not carry one, hence the fallback. */}
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {reference.source_name
                    ? `Linked in the source course to: "${reference.source_name}"`
                    : "The source course did not record which role play this was."}
                </Typography>
                <FormControl fullWidth error={!contentResolutions[reference.ref]}>
                  {/* labelId/id wiring is what associates the label with the control — without it
                      the select is unlabelled to a screen reader. */}
                  <InputLabel id={`content-ref-label-${reference.ref}`}>
                    Role play in this course
                  </InputLabel>
                  <Select
                    labelId={`content-ref-label-${reference.ref}`}
                    id={`content-ref-${reference.ref}`}
                    value={contentResolutions[reference.ref] || ""}
                    label="Role play in this course"
                    onChange={(event) => {
                      const nextValue = event.target.value;
                      setContentResolutions((prev) => ({ ...prev, [reference.ref]: nextValue }));
                    }}
                  >
                    <MenuItem value="">
                      <em>Select a role play</em>
                    </MenuItem>
                    {(reference.eligible || []).map((candidate) => (
                      <MenuItem key={candidate.id} value={candidate.id}>
                        {candidate.name || `Scenario ${candidate.id}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {(reference.eligible || []).length === 0 ? (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    This course has no role plays yet, so there is nothing to link to. Import the
                    role play first, then import this lesson.
                  </Alert>
                ) : null}
              </Box>
            ))}
          </Box>
        ) : null}

        {conflicts.length === 0 ? (
          unresolvedReferences.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {`No conflicting dependencies were found. Click "${resolvedConfirmLabel}" to proceed.`}
            </Typography>
          ) : null
        ) : (
          conflicts.map((conflict) => {
            const resolution = resolutions[conflict.ref] || {};
            const sourceMetadata = describeSourceMetadata(conflict);
            return (
              <Box
                key={conflict.ref}
                sx={{
                  mb: 3,
                  p: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1.5,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {conflict.source_name}
                  </Typography>
                  <Chip
                    size="small"
                    color="info"
                    label={TYPE_LABELS[conflict.type] || conflict.type}
                  />
                </Box>

                {sourceMetadata ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {sourceMetadata}
                  </Typography>
                ) : null}

                <RadioGroup
                  row
                  value={resolution.action || ""}
                  onChange={(event) => {
                    const nextAction = event.target.value;
                    setResolutions((prev) => ({
                      ...prev,
                      [conflict.ref]: {
                        ...prev[conflict.ref],
                        action: nextAction,
                        existing_id:
                          nextAction === "reuse_existing"
                            ? prev[conflict.ref]?.existing_id || conflict.default_existing_id || ""
                            : "",
                        new_name:
                          nextAction === "create_duplicate"
                            ? prev[conflict.ref]?.new_name || conflict.suggested_duplicate_name || ""
                            : prev[conflict.ref]?.new_name || conflict.suggested_duplicate_name || "",
                      },
                    }));
                  }}
                  sx={{ mb: 2 }}
                >
                  <FormControlLabel value="reuse_existing" control={<Radio />} label="Reuse existing" />
                  <FormControlLabel value="create_duplicate" control={<Radio />} label="Create duplicate" />
                </RadioGroup>

                {resolution.action === "reuse_existing" ? (
                  <FormControl fullWidth>
                    <InputLabel>Select Existing Item</InputLabel>
                    <Select
                      value={resolution.existing_id || ""}
                      label="Select Existing Item"
                      onChange={(event) => {
                        setResolutions((prev) => ({
                          ...prev,
                          [conflict.ref]: {
                            ...prev[conflict.ref],
                            existing_id: event.target.value,
                          },
                        }));
                      }}
                    >
                      <MenuItem value="">
                        <em>Select an existing item</em>
                      </MenuItem>
                      {conflict.existing_candidates?.map((candidate) => {
                        const extra = describeCandidate(conflict, candidate);
                        return (
                          <MenuItem key={candidate.id} value={candidate.id}>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {candidate.name}
                              </Typography>
                              {extra ? (
                                <Typography variant="caption" color="text.secondary">
                                  {extra}
                                </Typography>
                              ) : null}
                            </Box>
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                ) : null}

                {resolution.action === "create_duplicate" ? (
                  <TextField
                    fullWidth
                    label="Duplicate Name"
                    value={resolution.new_name || ""}
                    onChange={(event) => {
                      setResolutions((prev) => ({
                        ...prev,
                        [conflict.ref]: {
                          ...prev[conflict.ref],
                          new_name: event.target.value,
                        },
                      }));
                    }}
                  />
                ) : null}
              </Box>
            );
          })
        )}

        {validationErrors.length > 0 ? (
          <Alert severity="warning">
            {validationErrors[0]}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={loading || validationErrors.length > 0}
        >
          {loading ? "Importing..." : resolvedConfirmLabel}
        </Button>
      </DialogActions>
      </>}
    </Dialog>
  );
};

export default DependencyResolutionDialog;
